import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, User, MessageSquare, Send, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';

const ContactForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(''); // 'success', 'error', or ''

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('');

    try {
      // Configuration EmailJS - Ces valeurs devront être configurées
      const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      console.log('EmailJS Config:', { 
        serviceID: serviceID ? 'SET' : 'MISSING', 
        templateID: templateID ? 'SET' : 'MISSING', 
        publicKey: publicKey ? 'SET' : 'MISSING' 
      });

      if (!serviceID || !templateID || !publicKey) {
        console.error('EmailJS configuration missing:', { serviceID, templateID, publicKey });
        throw new Error('EmailJS configuration missing');
      }

      const templateParams = {
        from_name: `${formData.name} (Contact Support)`,
        from_email: formData.email,
        email: 'lenka.galinie@gmail.com',  // Destinataire direct - Lenka
        to_email: 'lenka.galinie@gmail.com', // Assurer l'envoi à Lenka
        subject: `📞 Contact Support - ${formData.subject}`,
        message: `Nouveau message de contact pour 123zzz

📝 INFORMATIONS DE CONTACT:
• Nom: ${formData.name}
• Email: ${formData.email}
• Sujet: ${formData.subject}

💬 MESSAGE:
${formData.message}

📅 DÉTAILS DU CONTACT:
• Date: ${new Date().toLocaleDateString('fr-FR')}
• Heure: ${new Date().toLocaleTimeString('fr-FR')}

Email de réponse: ${formData.email}`,
        date: new Date().toLocaleDateString('fr-FR'),
        time: new Date().toLocaleTimeString('fr-FR')
      };

      console.log('Template Parameters:', templateParams);

      await emailjs.send(serviceID, templateID, templateParams, publicKey);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Fermer le modal après 2 secondes
      setTimeout(() => {
        onClose();
        setStatus('');
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="bg-white/90 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 border-b border-white/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{t('contact.title')}</h2>
                <p className="text-slate-600 text-sm">{t('contact.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
            >
              <X className="w-6 h-6 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-6">
          {status === 'success' && (
            <motion.div 
              className="mb-6 p-4 bg-green-100/80 border border-green-200 rounded-2xl flex items-center space-x-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Check className="w-5 h-5 text-green-600" />
              <p className="text-green-800 font-medium">{t('contact.success')}</p>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div 
              className="mb-6 p-4 bg-red-100/80 border border-red-200 rounded-2xl flex items-center space-x-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <X className="w-5 h-5 text-red-600" />
              <p className="text-red-800 font-medium">{t('contact.error')}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('contact.name')} *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.name_placeholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('contact.email')} *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.email_placeholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* Subject Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('contact.subject')} *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder={t('contact.subject_placeholder')}
                className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Message Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('contact.message')} *
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder={t('contact.message_placeholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/60 backdrop-blur-sm border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                />
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{t('contact.sending')}</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>{t('contact.send')}</span>
                </>
              )}
            </motion.button>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-blue-50/80 border border-blue-200 rounded-xl">
            <p className="text-blue-800 text-sm text-center">
              {t('contact.info')}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContactForm;